import React from "react";
import { Helper } from "@utils/index";
import { useGeneralSettings } from "@hooks/useGeneralSettings";
import {
  StylePriceBreakdownGrid,
  SectionCard,
  SectionTitle,
  Row,
  RowLabel,
  RowValue,
  TotalRow,
  TotalLabel,
  TotalValue,
} from "./style";
import styled from "@emotion/styled";

// Commission breakdown styled components
const CommissionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-child {
    border-bottom: none;
  }
`;

const CommissionProductImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
`;

const CommissionProductImagePlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: #9ca3af;
  border: 1px solid #e5e7eb;
`;

const CommissionProductInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CommissionProductName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CommissionSourceBadge = styled.span<{ source?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  margin-top: 3px;
  background: ${({ source }) =>
    source === "subcategory"
      ? "#ede9fe"
      : source === "category"
        ? "#dbeafe"
        : "#fef3c7"};
  color: ${({ source }) =>
    source === "subcategory"
      ? "#7c3aed"
      : source === "category"
        ? "#2563eb"
        : "#d97706"};
`;

const CommissionAmounts = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const CommissionAmountValue = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #dc2626;
`;

const CommissionPayoutValue = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`;

const CommissionPercentTag = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #9ca3af;
  margin-left: 4px;
`;

export const PriceBreakdown = ({
  breakdown,
  commissionBreakdown,
  bucketUrl,
}: any) => {
  const { settings } = useGeneralSettings();

  const customerRows = [
    ["Product Total (MRP)", breakdown.originalAmount],
    ["Active Deal Discount", breakdown.activeDealDiscount],
    ["Discount", breakdown.discount],
    ["Product Subtotal", breakdown.productAmountAfterDiscount],
    ["Coupon Discount", breakdown.couponDiscount],
    ["Coupon (by Store)", breakdown.couponPaidByStore],
    ["Coupon (by Admin)", breakdown.couponPaidByAdmin],
    ["Platform Fee", breakdown.processingPrice],
    ["Delivery Charge", breakdown.deliveryCharge],
    ["Delivery Man Tip", breakdown.deliveryManTip],
  ].filter(([label, value]: any) => {
    if (["Product Total (MRP)", "Product Subtotal"].includes(label as string))
      return true;
    return value !== 0 && value !== undefined;
  });

  const storeDeductions = [
    ["Commission (from Store)", breakdown.commissionCharge],
  ].filter(([_, value]: any) => value !== 0 && value !== undefined);

  const financialRows = [
    ["Store Earnings", breakdown.storeEarnings],
    ["Admin Earnings", breakdown.adminEarnings],
    ["Customer Savings", breakdown.userSavings],
  ].filter(([_, value]: any) => value !== undefined && value !== null);

  const commissionItems = commissionBreakdown?.items || [];

  return (
    <StylePriceBreakdownGrid>
      {/* Customer Breakdown */}
      <SectionCard>
        <SectionTitle>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
          Order Breakdown
        </SectionTitle>
        {customerRows.map(([label, value]) => {
          const isDiscount =
            label === "Discount" ||
            label === "Active Deal Discount" ||
            label.includes("Coupon");
          const isSubtotal = label === "Product Subtotal";
          const isCouponBreakdown =
            label === "Coupon (by Store)" || label === "Coupon (by Admin)";

          return (
            <Row key={label} indent={isCouponBreakdown}>
              <RowLabel
                bold={isSubtotal}
                small={isCouponBreakdown}
                muted={isCouponBreakdown}
              >
                {label}
              </RowLabel>
              <RowValue
                color={isDiscount ? "#10b981" : undefined}
                bold={isSubtotal}
                small={isCouponBreakdown}
              >
                {isDiscount && !isCouponBreakdown && value > 0 ? "- " : ""}
                {Helper.formatCurrency(Math.abs(value), settings)}
              </RowValue>
            </Row>
          );
        })}
      </SectionCard>

      {/* Customer Pays Total */}
      <TotalRow>
        <TotalLabel>Customer Pays</TotalLabel>
        <TotalValue>
          {Helper.formatCurrency(breakdown.customerTotal, settings)}
        </TotalValue>
      </TotalRow>

      {/* Partial Payment Breakdown */}
      {breakdown.paidAmount > 0 && (
        <div
          style={{
            marginTop: "12px",
            borderTop: "1px dashed #E2E8F0",
            paddingTop: "12px",
          }}
        >
          <Row>
            <RowLabel color="#059669" bold>
              Amount Paid (Partial)
            </RowLabel>
            <RowValue color="#059669" bold>
              {Helper.formatCurrency(breakdown.paidAmount, settings)}
            </RowValue>
          </Row>
          <Row>
            <RowLabel color="#dc2626" bold>
              Balance Due
            </RowLabel>
            <RowValue color="#dc2626" bold>
              {Helper.formatCurrency(breakdown.dueAmount, settings)}
            </RowValue>
          </Row>
        </div>
      )}

      {/* Commission Breakdown per Product */}
      {commissionItems.length > 0 && (
        <SectionCard variant="deduction">
          <SectionTitle color="#7c3aed">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#7c3aed"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
              <line x1="7" y1="7" x2="7.01" y2="7" />
            </svg>
            Commission Breakdown
            <CommissionPercentTag>
              Total:{" "}
              {Helper.formatCurrency(
                commissionBreakdown?.totalCommission || 0,
                settings,
              )}
            </CommissionPercentTag>
          </SectionTitle>
          {commissionItems.map((item: any, index: number) => (
            <CommissionItem key={item.orderItemId || index}>
              {item.product?.image ? (
                <CommissionProductImage
                  src={`${bucketUrl}/${item.product.image}`}
                  alt={item.product?.name || "Product"}
                  onError={(e: any) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <CommissionProductImagePlaceholder>
                  📦
                </CommissionProductImagePlaceholder>
              )}
              <CommissionProductInfo>
                <CommissionProductName>
                  {item.product?.name || `Product #${item.productId}`}
                </CommissionProductName>
                <CommissionSourceBadge source={item.commissionSource}>
                  {item.commissionSource}
                  {item.commissionSourceName
                    ? `: ${item.commissionSourceName}`
                    : ""}
                </CommissionSourceBadge>
              </CommissionProductInfo>
              <CommissionAmounts>
                <CommissionAmountValue>
                  -{" "}
                  {Helper.formatCurrency(item.commissionAmount || 0, settings)}
                  <CommissionPercentTag>
                    ({item.commissionPercent}%)
                  </CommissionPercentTag>
                </CommissionAmountValue>
                <CommissionPayoutValue>
                  Payout:{" "}
                  {Helper.formatCurrency(item.payoutAmount || 0, settings)}
                </CommissionPayoutValue>
              </CommissionAmounts>
            </CommissionItem>
          ))}
        </SectionCard>
      )}

      {/* Store Deductions (legacy fallback when no commission items) */}
      {storeDeductions.length > 0 && commissionItems.length === 0 && (
        <SectionCard variant="deduction">
          <SectionTitle color="#dc2626">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#dc2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
            Store Deductions
          </SectionTitle>
          {storeDeductions.map(([label, value]) => (
            <Row key={label}>
              <RowLabel>{label}</RowLabel>
              <RowValue color="#dc2626" bold>
                - {Helper.formatCurrency(value, settings)}
              </RowValue>
            </Row>
          ))}
        </SectionCard>
      )}

      {/* Earnings Summary */}
      {financialRows.length > 0 && (
        <SectionCard variant="earnings">
          <SectionTitle color="#059669">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#059669"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
            Earnings Summary
          </SectionTitle>
          {financialRows.map(([label, value]) => (
            <Row key={label}>
              <RowLabel>{label}</RowLabel>
              <RowValue
                color={label === "Customer Savings" ? "#10b981" : undefined}
                bold
              >
                {Helper.formatCurrency(value, settings)}
              </RowValue>
            </Row>
          ))}
        </SectionCard>
      )}
    </StylePriceBreakdownGrid>
  );
};
